import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, imgSrc, formatDate } from "@/lib/api";
import { MaskedLines, EASE } from "@/components/site/Motion";
import { CtaBand } from "@/components/home/CtaBand";

export default function NewsArticle() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setItem(null);
    setError(false);
    api.get(`/news/${slug}`).then((r) => setItem(r.data ?? null)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <section className="max-w-3xl mx-auto px-6 py-40" data-testid="news-article-error">
        <h1 className="font-display text-4xl font-bold">Nyheten hittades inte.</h1>
        <Link to="/nyheter" className="inline-flex items-center gap-2 mt-6 font-semibold text-[#0B14A0]"><ArrowLeft className="w-4 h-4" />Alla nyheter</Link>
      </section>
    );
  }
  if (!item) return <div className="min-h-[60vh]" />;

  const paragraphs = (item.content || "").split(/\n+/).filter(Boolean);

  return (
    <article data-testid="news-article-page">
      <header className="-mt-20 pt-32 lg:pt-40 pb-12 grid-lines">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/nyheter" data-testid="news-article-back" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B14A0]"><ArrowLeft className="w-4 h-4" />Alla nyheter</Link>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-[#0B14A0]">{item.category} · {formatDate(item.created_at)}</p>
          <MaskedLines testId="news-article-title" className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mt-5" lines={[item.title]} />
          {item.excerpt && <p className="mt-6 text-lg sm:text-xl text-[#3A3F5C] leading-relaxed">{item.excerpt}</p>}
        </div>
      </header>
      {item.image_url && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: EASE, delay: 0.3 }} className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="clip-notch relative aspect-[16/9] overflow-hidden rounded-[28px] bg-[#070B2E]">
            <img src={imgSrc(item.image_url)} alt={item.title} className="absolute inset-0 w-full h-full object-cover" data-testid="news-article-image" />
          </div>
        </motion.div>
      )}
      <div className="max-w-3xl mx-auto px-6 py-16 prose-redoc text-[17px] text-[#1C2150]" data-testid="news-article-content">
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <CtaBand title={["Vill du veta", "mer?"]} />
    </article>
  );
}
